import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PageLoader } from '@/components/LazyPages';

/**
 * SECURITY: מצב פיתוח נבדק אך ורק דרך import.meta.env.DEV
 * שמוחזר כ-false בבילד פרודקשן על ידי Vite.
 */
const IS_DEV_BUILD = import.meta.env.DEV === true;

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
  /** כשtrue, גסט (לא מחובר) יכול לראות את התוכן ללא הפניה ל-login */
  guestAllowed?: boolean;
}

const ProtectedRoute = ({ children, requireOnboarding = true, guestAllowed = false }: ProtectedRouteProps) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  
  // SECURITY: דילוג על אימות אפשרי **רק** בסביבת פיתוח
  // import.meta.env.DEV מוחזר כ-false בכל בילד פרודקשן
  // localStorage נקרא רק בתוך בלוק DEV כדי למנוע כל גישה בפרודקשן
  let canSkipAuth = false;
  if (IS_DEV_BUILD) {
    canSkipAuth = (
      new URLSearchParams(window.location.search).get('skipAuth') === 'true' || 
      localStorage.getItem('sy_dev_skip_auth') === 'true'
    );
  }

  // Neutral spinner — Layout is the parent, so header + bottom-nav stay
  // mounted and only the body slot transitions through this state.
  if (isLoading) {
    return <PageLoader />;
  }

  // מצב פיתוח בלבד - דילוג על אימות
  if (canSkipAuth) {
    return <>{children}</>;
  }

  // לא מחובר
  if (!isAuthenticated) {
    // אם גסט מורשה — הצגת התוכן ללא הפניה
    if (guestAllowed) {
      return <>{children}</>;
    }
    // Preserve the intended destination so /login can round-trip back to it
    // after auth — needed for deep links (email/notification) that land on an
    // auth-gated screen while the user is signed out. See src/pages/Login.tsx.
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // מחובר אבל לא השלים תהליך ראשוני - הפניה לאונבורדינג
  if (requireOnboarding && user && !user.hasCompletedOnboarding) {
    return <Navigate to="/onboarding/0" replace />;
  }

  // הכל בסדר - הצגת התוכן
  return <>{children}</>;
};

export default ProtectedRoute;
